import axios from 'axios';
import API from "../config/API";
import siteSetting from "../config/env";
import { clearAllConciergeSessions } from "./conciergeSessionStore";

export const userService = {
    login,
    logout,
    signup,
    getProfile,
    getClientProfile,
    forgot,
    reset,
    changePassword,
    editClientProfile,
    socialLogin,
    sendSignupVerificationCode,
    verifySignupCode,
    resetPasswordAndLogin,
    checkEmailExists,
    sendProSignupCode,
    verifyProSignupCode,
    proRegister,
    linkSubscriptionBilling,
    getSubscriptionStatus,
    getPartnerTermsStatus,
    acceptPartnerTerms,
    setFirstPassword
};

// Feeds the app-gate. Full v2 URL, so plain axios (not the v1 `instance`).
// Claim flow (spec §5): set the FIRST password on a passwordless account.
// v2 endpoint — the v2 middleware reads the custom accessToken header.
function setFirstPassword(params) {
    return axios.post(API.SET_FIRST_PASSWORD, params, {
        headers: {
            'Content-Type': 'application/json',
            'accessToken': localStorage.getItem('accessToken') || ''
        }
    });
}

function getSubscriptionStatus() {
    return axios.get(API.SUBSCRIPTION_STATUS, {
        headers: {
            'Content-Type': 'application/json',
            'accessToken': localStorage.getItem('accessToken') || ''
        }
    });
}

/**
 * Partner Terms acceptance state for the signed-in professional.
 * Resolves to { requiresAcceptance, currentVersion, acceptedVersion, acceptedAt }.
 * requiresAcceptance is TRUE only when the account is a self-employed
 * professional AND has no acceptance on record for the current published
 * version. See PARTNER-TERMS-GATE.md.
 */
function getPartnerTermsStatus() {
    return axios.get(API.PARTNER_TERMS_STATUS, {
        headers: {
            'Content-Type': 'application/json',
            'accessToken': localStorage.getItem('accessToken') || ''
        }
    });
}

/**
 * Record acceptance of the current Partner Terms version. `context` tells the
 * audit trail where it happened (e.g. 'se_welcome_gate').
 */
function acceptPartnerTerms(context = 'se_welcome_gate') {
    return axios.post(API.PARTNER_TERMS_ACCEPT, { context }, {
        headers: {
            'Content-Type': 'application/json',
            'accessToken': localStorage.getItem('accessToken') || ''
        }
    });
}

// v2 session endpoints answer { statusCode, responseData | error }. The login,
// header and settings screens were written against the v1 envelope
// { errorCode, data, messages }, so present the v2 reply in that shape
// (keeping the v2 fields alongside) rather than touch every caller.
export function toLegacyEnvelope(response) {
    const body = response && response.data;
    if (!body || typeof body !== 'object' || body.errorCode !== undefined) return response;
    if (body.statusCode === 1) {
        const data = body.responseData || {};
        response.data = Object.assign({}, body, { errorCode: 0, data: data, messages: data.message });
    } else if (body.statusCode === 0) {
        const error = body.error || {};
        response.data = Object.assign({}, body, {
            errorCode: error.errorCode || 1,
            data: [],
            messages: error.responseMessage || 'Something went wrong. Please try again.'
        });
    }
    return response;
}

function v2Headers() {
    return {
        'Content-Type': 'application/json',
        'accessToken': localStorage.getItem('accessToken') || ''
    };
}

// v2: ends this session server-side (deletes the token), then clears the device.
function logout(apiParam,params) {
    return axios.put(API.LOGOUT + (params && params.userId ? params.userId : 'me'), {}, { headers: v2Headers() })
    .then((response) => {
        localStorage.clear();
        // The booking page's chat lives in sessionStorage, which the line
        // above doesn't touch: a signed-in client's conversation must not be
        // left for whoever uses the device next.
        clearAllConciergeSessions();
        return toLegacyEnvelope(response);
    })
    .catch((err) => {
        localStorage.clear();
        clearAllConciergeSessions();
    })
}

function login(params) {
    return axios.post(API.LOGIN, {
        email: params.email,
        password: params.password,
        deviceToken: params.deviceToken,
        deviceTypeId: params.deviceTypeId,
        latitude: params.latitude,
        longitude: params.longitude
    }).then(toLegacyEnvelope)
}

// Sign in with Apple (v2). Web: { code, state } from the /redirect page.
// iOS app: { identityToken }. To finish a private-relay sign-up add the
// user's own { email, first_name, last_name, phoneNumber }. The server takes
// the Apple ID and Apple's email from its own verified record — never from here.
function socialLogin(params) {
    const body = {};
    ['code', 'state', 'identityToken', 'email', 'first_name', 'last_name', 'phoneNumber',
        'deviceToken', 'deviceTypeId', 'latitude', 'longitude'].forEach((k) => {
        if (params[k] !== undefined && params[k] !== null && params[k] !== '') body[k] = params[k];
    });
    return axios.post(API.USER_SOCIAL_LOGIN, body).then(toLegacyEnvelope)
}

function signup(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    // v2 client signup. The v2 endpoint reconciles the email against
    // salon-created accounts (users.email AND salonclients overrides) BEFORE
    // creating anything, so a client whose salon already made them an account
    // claims it instead of getting a duplicate with their bookings stranded.
    //
    // Adapted here to the legacy v1 response dialects so every existing
    // caller (redux action token storage, ClientSignup / ClientSignupEnhanced
    // statusCode checks, webHeader errorCode/messages checks) keeps working
    // untouched.
    return axios.post(API.CLIENT_SIGNUP_V2, params).then(function (response) {
        var result = (response && response.data && response.data.responseData) || {};

        if (result.outcome === 'created') {
            return { data: {
                statusCode: 1,
                errorCode: 0,
                data: {
                    tokenInfo: {
                        accessToken: result.accessToken,
                        refreshToken: result.refreshToken,
                        expiresIn: result.expiresIn,
                        tokenType: result.tokenType
                    },
                    userInfo: { userId: result.userId, name: result.name, email: result.email }
                }
            }};
        }

        if (result.outcome === 'claim_sent') {
            // "Check your email to finish signing up": a link is in their
            // inbox and nothing was created (only for a salon-entered address
            // on a placeholder login; real-email accounts get the code step).
            // Callers branch on `outcome`; messages/error cover the rest.
            return { data: {
                statusCode: 0,
                errorCode: 207,
                outcome: 'claim_sent',
                email: result.email,
                messages: result.message,
                error: { responseMessage: result.message }
            }};
        }

        if (result.outcome === 'verify_email' || result.outcome === 'claim_code_sent') {
            // beu-be client-flows §7.4: this email needs verifying before the
            // login is created (salon records under it sync in silently). A
            // code is on its way; nothing was created yet. ClientSignup shows
            // an ordinary "Check your email" code step and finishes with the
            // name + password already typed.
            return { data: {
                statusCode: 0,
                errorCode: 208,
                outcome: 'verify_email',
                messages: result.message,
                error: { responseMessage: result.message }
            }};
        }

        if (result.outcome === 'existing') {
            // Claimed account. Word it exactly like the legacy "email in use"
            // so ClientSignup's established recovery flow (verification code →
            // resetPasswordAndLogin) picks it up.
            return { data: {
                statusCode: 0,
                errorCode: 51,
                outcome: 'existing',
                messages: 'The email is already in use. Please provide another email to continue.',
                error: { responseMessage: 'The email is already in use. Please provide another email to continue.' }
            }};
        }

        // Anything else is a real API error (statusCode 0 + error envelope,
        // HTTP 200): rate limit, validation, server fault. It used to fall
        // through to "email already in use" — a brand-new email was told it
        // had an account and sent to the verification screen. Surface the
        // API's own message instead.
        const apiError = response && response.data && response.data.error;
        const message = (apiError && apiError.responseMessage) || 'Signup failed. Please try again.';
        return { data: {
            statusCode: 0,
            errorCode: (apiError && apiError.errorCode) || 0,
            outcome: 'error',
            messages: message,
            error: { responseMessage: message }
        }};
    });
}

function sendSignupVerificationCode(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.SEND_SIGNUP_VERIFICATION_CODE, params)
}

function verifySignupCode(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.VERIFY_SIGNUP_CODE, params)
}

function resetPasswordAndLogin(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.RESET_PASSWORD_AND_LOGIN, params)
}

function checkEmailExists(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.CHECK_EMAIL, params)
}

// v2 professional signup — pre-account SMS 2FA + account creation. Public
// (pre-auth); clientId is used for the auto-login token the register returns.
function sendProSignupCode(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.PRO_SIGNUP_SEND_CODE, params)
}

function verifyProSignupCode(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.PRO_SIGNUP_VERIFY_CODE, params)
}

function proRegister(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    return axios.post(API.PRO_REGISTER, params)
}

// Authed (v2 'accessToken' header) — link the subscription's Stripe customer to
// the just-created owner post-payment so marketing SMS overages etc. can bill.
function linkSubscriptionBilling(params) {
    let headers = {
        'Content-Type': 'application/json',
        accessToken: localStorage.getItem('accessToken') || ''
    };
    return axios.post(API.LINK_SUBSCRIPTION_BILLING, params, { headers })
}

// Which side this browser is on, for user/profile: callers pass
// defaultRedirectTo (most already did, unused), else the value saved at
// login / switch. The server otherwise answered from the account's stored
// setting, so switching to client on another device (the phone app) turned an
// open business session into a client one on its next refresh (2026-10-05).
export function profileMode(params) {
    let v = params && params.defaultRedirectTo;
    if (v === undefined || v === null || v === '') {
        try { v = localStorage.getItem('defaultRedirectTo'); } catch (e) { v = null; }
    }
    if (String(v) === '1') return 'business';
    if (String(v) === '0') return 'client';
    return undefined;
}

// v2 user/profile: the login payload for this token (no new token minted).
function getProfile(params) {
    const q = params || {};
    const mode = profileMode(q);
    return axios.get(API.USER_PROFILE, {
        params: { latitude: q.latitude, longitude: q.longitude, ...(mode ? { mode } : {}) },
        headers: v2Headers()
    }).then(toLegacyEnvelope);
}

function forgot(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    //console.log('params',params);
    return axios.post(API.USER_FORGOT, params)
}

function reset(params) {
    params.clientId=siteSetting.api_clientId;
    params.clientSecret=siteSetting.api_clientSecret;
    //console.log('params',params);
    return axios.post(API.USER_RESET, params)
}


// v2: needs the current password; signs out the account's other sessions.
function changePassword(params){
    return axios.put(API.USER_CHANGEPASSWORD, {
        oldPassword: params.oldPassword,
        newPassword: params.newPassword
    }, { headers: v2Headers() }).then(toLegacyEnvelope)
}


function editClientProfile(params) {
    // Don't override clientId if it's already set, otherwise use current user's ID
    if (!params.clientId) {
        params.clientId = localStorage.getItem('userId');
    }
    
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken,
    }
    
    return axios.post(API.CLIENT_EDIT, params, {headers: headers})
}

/**
 * Get client profile with stats (v2 API)
 * Returns user info, total bookings, total reviews, and member since year
 * This replaces the old v1 profile API + separate booking/review count calls
 */
function getClientProfile() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'accessToken': accessToken,
    }
    
    return axios.get(API.CLIENT_PROFILE, {headers: headers})
}